import { useMemo, useRef, useState, type ReactNode } from "react";
import { Braces, Sparkles } from "lucide-react";
import type { EmailVariable, VariableFill } from "@/api/email";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { tokenClass } from "@/components/email/TemplateText";
import { cn } from "@/lib/utils";

type Namespace = "contact" | "company" | "ai";

const TABS: { namespace: Namespace; label: string }[] = [
  { namespace: "contact", label: "Contact" },
  { namespace: "company", label: "Company" },
  { namespace: "ai", label: "AI" },
];

const GROUP_LABEL: Record<string, string> = { contact: "Contact", company: "Company", ai: "AI · this step" };

function useAllVariables(variables: EmailVariable[], aiKeys: string[]): EmailVariable[] {
  return useMemo(
    () => [
      ...variables,
      ...aiKeys.map((key) => ({
        token: `ai.${key}`,
        label: key.replace(/_/g, " "),
        description: "Generated for each contact from this step's prompt.",
        namespace: "ai",
        example: null,
      })),
    ],
    [variables, aiKeys],
  );
}

/** How often a variable has a value, and how that reads. */
function fillOf(fill: VariableFill | null | undefined, token: string) {
  if (!fill || !fill.considered || !(token in fill.filled)) return null;
  const share = fill.filled[token] / fill.considered;
  const percent = Math.round(share * 100);
  return {
    share,
    // Never "100%" while a contact lacks it, never "0%" while one has it.
    label: `${share < 1 && percent === 100 ? 99 : share > 0 && percent === 0 ? 1 : percent}%`,
    detail: `${fill.filled[token].toLocaleString()} of ${fill.considered.toLocaleString()} have a value`,
    tone: share >= 0.95 ? "text-slate-400" : share >= 0.5 ? "text-amber-600" : "text-rose-600",
  };
}

function populationLabel(fill: VariableFill): string {
  const n = fill.total.toLocaleString();
  const sample = fill.considered < fill.total ? ` (sample of ${fill.considered.toLocaleString()})` : "";
  return fill.population === "campaign"
    ? `Filled for the campaign's ${n} contact${fill.total === 1 ? "" : "s"}${sample}`
    : `Filled for the project's ${n} contacts with an email${sample}`;
}

/** The variables a template may use: one tab per source (contact, company,
 *  this step's AI passages) with how many each holds, and a search across
 *  all of them. A click inserts; with the fill rate known, a variable some
 *  contacts lack can be inserted with a fallback text. */
export function VariableList({
  variables,
  aiKeys = [],
  fill,
  onPick,
  className,
  autoFocus,
  inlineFallbacks = true,
}: {
  variables: EmailVariable[];
  aiKeys?: string[];
  /** How often each variable has a value (contact and company ones). */
  fill?: VariableFill | null;
  onPick: (token: string, withFallback?: boolean) => void;
  className?: string;
  autoFocus?: boolean;
  inlineFallbacks?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState<Namespace>("contact");
  const all = useAllVariables(variables, aiKeys);
  const counts = useMemo(() => {
    const result: Record<Namespace, number> = { contact: 0, company: 0, ai: 0 };
    for (const v of all) if (v.namespace in result) result[v.namespace as Namespace] += 1;
    return result;
  }, [all]);
  const tabs = TABS.filter((t) => counts[t.namespace] > 0);
  const q = query.trim().toLowerCase();
  const items = q
    ? all.filter(
        (v) => v.token.toLowerCase().includes(q) || v.label.toLowerCase().includes(q) || v.description.toLowerCase().includes(q),
      )
    : all.filter((v) => v.namespace === tab);

  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <Input
        autoFocus={autoFocus}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search all variables…"
        aria-label="Search variables"
        className="h-8 rounded-lg border-slate-200 bg-slate-50/60 text-xs"
      />
      {!q && (
        <div role="tablist" aria-label="Variable source" className="mt-2 flex gap-1 rounded-lg bg-slate-100/80 p-0.5">
          {tabs.map((t) => {
            const active = t.namespace === tab;
            return (
              <button
                key={t.namespace}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.namespace)}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors",
                  active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700",
                )}
              >
                {t.namespace === "ai" && <Sparkles className="h-3 w-3 text-fuchsia-500" />}
                {t.label}
                <span
                  className={cn(
                    "rounded-full px-1.5 text-[10px] tabular-nums",
                    active ? "bg-indigo-50 text-indigo-600" : "bg-slate-200/70 text-slate-500",
                  )}
                >
                  {counts[t.namespace]}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {fill && fill.considered > 0 && (
        <p className="mt-2 px-1 text-[10.5px] leading-snug text-slate-400">
          {populationLabel(fill)}. {inlineFallbacks ? "Without a value, a contact is not sent unless the variable has a fallback." : "Valeurs sourcées. Une donnée manquante bloque la sauvegarde ; seuls les passages expressément omis restent facultatifs."}
        </p>
      )}
      <div className="mt-1.5 min-h-0 flex-1 overflow-y-auto pr-1">
        {q && (
          <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.07em] text-slate-400">
            {items.length} {items.length === 1 ? "match" : "matches"} in all sources
          </p>
        )}
        <ul className="space-y-0.5" role={q ? undefined : "tabpanel"}>
          {items.map((variable) => {
            const rate = fillOf(fill, variable.token);
            return (
              <li key={variable.token} className="group/row flex items-center gap-1 rounded-md hover:bg-indigo-50/60">
                <button
                  type="button"
                  onClick={() => onPick(variable.token)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1.5 py-1.5 text-left"
                  title={variable.example ? `${variable.description} — e.g. ${variable.example}` : variable.description}
                >
                  <span className="min-w-0 flex-1 truncate text-xs">
                    <span className="font-medium text-slate-700 group-hover/row:text-indigo-700">{variable.label}</span>
                    {variable.example && <span className="ml-1.5 text-slate-400">{variable.example}</span>}
                  </span>
                  {q && (
                    <span className="shrink-0 rounded bg-slate-100 px-1 text-[9.5px] font-medium uppercase tracking-wide text-slate-500">
                      {GROUP_LABEL[variable.namespace] ?? variable.namespace}
                    </span>
                  )}
                  {rate && (
                    <span className={cn("shrink-0 text-[10.5px] font-semibold tabular-nums", rate.tone)} title={rate.detail}>
                      {rate.label}
                    </span>
                  )}
                  <span
                    className={cn(
                      "max-w-[48%] shrink-0 truncate rounded px-1 font-mono text-[10.5px] leading-[18px] ring-1 ring-inset",
                      tokenClass(variable.token),
                    )}
                  >
                    {variable.token}
                  </span>
                </button>
                {inlineFallbacks && rate && rate.share < 1 && (
                  <button
                    type="button"
                    onClick={() => onPick(variable.token, true)}
                    title={`Insert {{${variable.token} | "…"}}: the text to use when a contact has no value`}
                    className="mr-1 shrink-0 rounded px-1.5 py-0.5 text-[10.5px] font-medium text-amber-700 opacity-0 ring-1 ring-inset ring-amber-200 transition-opacity hover:bg-amber-50 focus-visible:opacity-100 group-hover/row:opacity-100"
                  >
                    + fallback
                  </button>
                )}
              </li>
            );
          })}
        </ul>
        {!items.length && <p className="px-1 py-4 text-center text-xs text-slate-400">No variable matches.</p>}
      </div>
    </div>
  );
}

/** The "Variables" button with the list in a popover. */
export function VariablePicker({
  variables,
  aiKeys,
  fill,
  onPick,
  children,
  label = "Variables",
  inlineFallbacks = true,
}: {
  variables: EmailVariable[];
  aiKeys?: string[];
  fill?: VariableFill | null;
  onPick: (token: string, withFallback?: boolean) => void;
  children?: ReactNode;
  label?: string;
  inlineFallbacks?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // After a pick the caller puts the caret in its field: the popover must not
  // hand the focus back to its button (a key typed next would land there).
  const picked = useRef(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <button
            type="button"
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:border-indigo-200 hover:text-indigo-700"
          >
            <Braces className="h-3.5 w-3.5" />
            {label}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex h-[420px] w-[380px] flex-col p-3"
        onCloseAutoFocus={(event) => {
          if (!picked.current) return;
          picked.current = false;
          event.preventDefault();
        }}
      >
        <VariableList
          autoFocus
          inlineFallbacks={inlineFallbacks}
          variables={variables}
          aiKeys={aiKeys}
          fill={fill}
          onPick={(token, withFallback) => {
            picked.current = true;
            onPick(token, withFallback);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
